import { effect, inject, Injectable } from '@angular/core';

import { AuthStore } from '../../store/auth.store';
import { PopupService } from './popup.service';
import { LocalStorageService } from './local-storage.service';
import { PopupAcknowledgeRequestModel } from '../models/popups/popupAcknowledgeRequest.model';
import { PendingPopupsModel } from '../models/popups/pendingPopups.model';

const POPUP_CACHE_KEY = 'POPUPS_PENDING_CACHE';
const CACHE_DURATION_MS = 1 * 60 * 60 * 1000; // 1 hour

interface PopupCache {
  pending: PendingPopupsModel;
  checkedAt: number;
}

const NO_PENDING_POPUPS: PendingPopupsModel = {
  atrPopupPending: false,
  bulkDeletePopupPending: false,
};

@Injectable({
  providedIn: 'root',
})
export class PopupSessionService {
  private readonly api = inject(PopupService);
  private readonly authStore = inject(AuthStore);
  private readonly localStorageService = inject(LocalStorageService);

  private checked = false;
  private pending: PendingPopupsModel = { ...NO_PENDING_POPUPS };
  private previousLicenseKey: string | null = null;

  constructor() {
    effect(() => {
      const licenseKey = (this.authStore.account()?.licenses ?? [])
        .map((license) => license.name)
        .sort()
        .join(',');

      if (this.previousLicenseKey !== null && licenseKey !== this.previousLicenseKey) {
        this.checked = false;
      }

      this.previousLicenseKey = licenseKey;
    });
  }

  public async checkPending(): Promise<PendingPopupsModel> {
    const identityId = this.authStore.account()?.identityId;

    if (!identityId) {
      return { ...NO_PENDING_POPUPS };
    }

    if (this.checked) {
      return this.pending;
    }

    const cached = this.localStorageService.getUserScopedItem(
      POPUP_CACHE_KEY,
      identityId
    ) as PopupCache | null;

    const now = Date.now();

    if (
      cached &&
      typeof cached.checkedAt === 'number' &&
      now - cached.checkedAt < CACHE_DURATION_MS &&
      cached.pending &&
      typeof cached.pending.atrPopupPending === 'boolean' &&
      typeof cached.pending.bulkDeletePopupPending === 'boolean'
    ) {
      this.pending = { ...cached.pending };
      this.checked = true;
      return this.pending;
    }

    try {
      this.pending = await this.api.checkPending();

      const cache: PopupCache = {
        pending: this.pending,
        checkedAt: Date.now(),
      };

      this.localStorageService.setUserScopedItem(POPUP_CACHE_KEY, cache, identityId);
    } catch {
      // Do not cache failures for 1 hour; allow a later retry.
      this.pending = { ...NO_PENDING_POPUPS };
      return this.pending;
    }

    this.checked = true;
    return this.pending;
  }

  public async acknowledge(request: PopupAcknowledgeRequestModel): Promise<void> {
    if (request.atrPopupAcknowledged) {
      this.pending.atrPopupPending = false;
    }

    if (request.bulkDeletePopupAcknowledged) {
      this.pending.bulkDeletePopupPending = false;
    }

    let checkedAt = Date.now();

    try {
      await this.api.acknowledge(request);
    } catch {
      // Expire the cache so the next page load asks the backend again.
      checkedAt = 0;
    }

    const identityId = this.authStore.account()?.identityId;

    if (identityId && this.checked) {
      const cache: PopupCache = {
        pending: { ...this.pending },
        checkedAt,
      };

      this.localStorageService.setUserScopedItem(POPUP_CACHE_KEY, cache, identityId);
    }
  }
}
